import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Modal, useInitialFocus } from '@playerbase/ui';
import type { DiscordGuildChannel } from '@playerbase/types';

// ---------------------------------------------------------------------------
// AddChannelModal - the picker dialog.
//
// Lists the connection's mirrored channels grouped by parent category so
// the layout matches how Discord itself displays them. Channels already in
// the watched set are visually disabled (story requirement: "channels
// already being watched are visibly marked and not selectable again").
//
// The Refresh button fires a NOTIFY that asks the bot to re-fetch the
// channel list - useful when the dev just gave the bot a role on a
// previously-private channel and wants it to appear without waiting on a
// gateway event.
// ---------------------------------------------------------------------------

type AddChannelModalProps = {
  open: boolean;
  channels: DiscordGuildChannel[];
  alreadyWatchedIds: ReadonlySet<string>;
  // For channels watched by other games in the same studio, the list of
  // those games' slugs. Rendered as a subtle hint so studios with
  // multiple games on one Discord server know who's already watching
  // what. Optional - studios with a single game pass no map.
  otherGameSlugsByChannelId?: ReadonlyMap<string, string[]>;
  busy: boolean;
  refreshing: boolean;
  errorMessage: string | null;
  // backfillDays is 0 (forward-only) or 1-30 (also pull historical
  // messages from each new channel back N days). Bot streams those
  // messages through the inspector pipeline; in this PR they don't
  // persist, but the JSONL corpus they produce informs the Pool design.
  onConfirm: (channelIds: string[], backfillDays: number) => void;
  onCancel: () => void;
  onRefresh: () => void;
};

const MAX_BACKFILL_DAYS = 30;

function clampDays(raw: string): number {
  if (raw === '') return 0;
  const n = Number.parseInt(raw, 10);
  if (Number.isNaN(n) || n < 0) return 0;
  if (n > MAX_BACKFILL_DAYS) return MAX_BACKFILL_DAYS;
  return n;
}

interface Group {
  parentName: string;
  channels: DiscordGuildChannel[];
}

// Group channels by parent_name. Top-level (parent_name = null) goes
// first. Threads are filtered out upstream so groups only contain
// channels the bot can actually watch as a unit.
function groupByParent(channels: DiscordGuildChannel[]): Group[] {
  const groups = new Map<string, DiscordGuildChannel[]>();
  const topLevelKey = '__top__';
  for (const c of channels) {
    const key = c.parentName ?? topLevelKey;
    const list = groups.get(key);
    if (list === undefined) groups.set(key, [c]);
    else list.push(c);
  }
  const out: Group[] = [];
  if (groups.has(topLevelKey)) {
    out.push({ parentName: 'Top level', channels: groups.get(topLevelKey)! });
    groups.delete(topLevelKey);
  }
  for (const [name, list] of [...groups.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    out.push({ parentName: name, channels: list });
  }
  return out;
}

function channelTypeBadge(type: DiscordGuildChannel['type']): string {
  switch (type) {
    case 'forum':
      return 'Forum';
    case 'announcement':
      return 'Announcements';
    case 'thread':
      return 'Thread';
    case 'text':
    default:
      return 'Text';
  }
}

export function AddChannelModal({
  open,
  channels,
  alreadyWatchedIds,
  otherGameSlugsByChannelId,
  busy,
  refreshing,
  errorMessage,
  onConfirm,
  onCancel,
  onRefresh,
}: AddChannelModalProps): JSX.Element {
  const cancelRef = useInitialFocus<HTMLButtonElement>();
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  // Days input is held as a string so the user can clear it mid-edit
  // without React forcing it back to "0". Coerced to a clamped number on
  // submit and any time we surface it to other UI.
  const [backfillDaysInput, setBackfillDaysInput] = useState('0');

  // Reset selection + days every time the modal opens so reopening doesn't
  // retain a stale tick or a leftover backfill window the user already
  // submitted.
  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setBackfillDaysInput('0');
    }
  }, [open]);

  // Threads (forum posts and ad-hoc reply threads alike) aren't sensible
  // watch targets - watching a forum picks up its posts already, and
  // ad-hoc threads are too ephemeral to subscribe to individually.
  const pickableChannels = useMemo(
    () => channels.filter((c) => c.type !== 'thread'),
    [channels],
  );
  const groups = useMemo(() => groupByParent(pickableChannels), [pickableChannels]);
  const noPickable = pickableChannels.every((c) => alreadyWatchedIds.has(c.channelId));

  const toggle = (channelId: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(channelId)) next.delete(channelId);
      else next.add(channelId);
      return next;
    });
  };

  return (
    <Modal
      open={open}
      onClose={onCancel}
      busy={busy}
      labelledById="add-channel-title"
      initialFocusRef={cancelRef}
    >
      <Card>
        <Card.Header>
          <Card.Title id="add-channel-title">Add channels to watch</Card.Title>
          <Card.Description>
            Pick one or more channels in your server. The bot will start
            ingesting messages from them as soon as you confirm. Optionally
            also pull recent history from each channel.
          </Card.Description>
        </Card.Header>
        <Card.Content>
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-text-muted">
              {pickableChannels.length} channel{pickableChannels.length === 1 ? '' : 's'} visible to the bot.
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              disabled={refreshing || busy}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
          </div>

          {channels.length === 0 ? (
            <Alert tone="info" className="mt-4">
              The bot has no readable channels in this server. Make sure it has
              View Channel and Read Message History on at least one channel,
              then click Refresh.
            </Alert>
          ) : noPickable ? (
            <Alert tone="info" className="mt-4">
              All visible channels are already being watched.
            </Alert>
          ) : (
            <div className="mt-4 max-h-80 overflow-y-auto rounded-md border border-border">
              <ul className="divide-y divide-border">
                {groups.map((group) => (
                  <li key={group.parentName} className="px-3 py-2">
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">
                      {group.parentName}
                    </p>
                    <ul className="space-y-1">
                      {group.channels.map((c) => {
                        const already = alreadyWatchedIds.has(c.channelId);
                        const ticked = selected.has(c.channelId);
                        const otherWatchers =
                          otherGameSlugsByChannelId?.get(c.channelId) ?? [];
                        return (
                          <li key={c.channelId}>
                            <label
                              className={`flex cursor-pointer items-start gap-2 rounded px-1 py-1 text-sm ${
                                already ? 'cursor-not-allowed text-text-muted' : 'hover:bg-accent-bg'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={ticked}
                                disabled={already || busy}
                                onChange={() => toggle(c.channelId)}
                                className="mt-0.5"
                              />
                              <span className="flex-1 min-w-0">
                                <span className="block truncate">
                                  # {c.name}
                                </span>
                                {otherWatchers.length > 0 && (
                                  <span className="block text-xs text-text-muted">
                                    Also watched by: {otherWatchers.join(', ')}
                                  </span>
                                )}
                              </span>
                              <Badge tone={already ? 'neutral' : 'info'}>
                                {already ? 'Already watching' : channelTypeBadge(c.type)}
                              </Badge>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-4 rounded-md border border-border bg-surface-2 px-3 py-3">
            <label className="flex items-center gap-2 text-sm text-text">
              <span>Bring over history going back</span>
              <input
                type="number"
                min={0}
                max={MAX_BACKFILL_DAYS}
                step={1}
                value={backfillDaysInput}
                onChange={(e) => {
                  // Clamp on every keystroke so the user sees the cap take
                  // effect immediately rather than getting silently
                  // floor'd at submit. Empty + non-numeric pass through
                  // so the user can clear the field mid-edit.
                  const raw = e.target.value;
                  if (raw === '') {
                    setBackfillDaysInput('');
                    return;
                  }
                  const n = Number.parseInt(raw, 10);
                  if (Number.isNaN(n)) return;
                  if (n < 0) setBackfillDaysInput('0');
                  else if (n > MAX_BACKFILL_DAYS) setBackfillDaysInput(String(MAX_BACKFILL_DAYS));
                  else setBackfillDaysInput(String(n));
                }}
                disabled={busy}
                className="w-16 rounded-sm border border-border bg-surface px-2 py-1 text-sm text-text"
              />
              <span>days (max {MAX_BACKFILL_DAYS})</span>
            </label>
            <p className="mt-1 text-xs text-text-muted">
              Leave at 0 to only ingest messages posted from now on. Anything 1-{MAX_BACKFILL_DAYS} also
              streams existing messages from the selected channels into the inspector for that window.
            </p>
          </div>

          {errorMessage !== null && (
            <Alert tone="danger" className="mt-4">
              {errorMessage}
            </Alert>
          )}
        </Card.Content>
        <Card.Footer className="justify-end">
          <Button
            ref={cancelRef}
            variant="ghost"
            size="md"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            disabled={busy || selected.size === 0}
            onClick={() => onConfirm([...selected], clampDays(backfillDaysInput))}
          >
            {busy
              ? 'Adding…'
              : `Add ${selected.size === 0 ? '' : selected.size + ' '}channel${selected.size === 1 ? '' : 's'}`}
          </Button>
        </Card.Footer>
      </Card>
    </Modal>
  );
}
